import type { InputFieldDefinition } from "../renderer.types.ts";
import type { ChoiceFieldDefinition, NumberValidation, PublicFormField } from "../types.ts";
import type { NativeInputType, RenderContext } from "./types.ts";

/** A field wrapper plus the resolved DOM ids used to link its control and help text. */
export type FieldScaffold = {
  helpId: string | undefined;
  inputId: string;
  wrapper: HTMLDivElement;
};

/** Builds the `div.fb-field` wrapper and resolves the control and help-text ids. */
export const createFieldScaffold = (
  field: PublicFormField,
  context: RenderContext,
): FieldScaffold => {
  const wrapper = document.createElement("div");
  applyFieldAttributes(wrapper, field);

  const inputId = `${context.instanceId}-${toDomId(field.id)}`;
  let helpId: string | undefined;

  if (field.helpText) {
    helpId = `${inputId}-help`;
  }

  return { helpId, inputId, wrapper };
};

export const appendHelpText = (scaffold: FieldScaffold, field: PublicFormField): void => {
  if (field.helpText && scaffold.helpId) {
    scaffold.wrapper.append(createHelpText(field.helpText, scaffold.helpId));
  }
};

export const createControlLabel = (
  label: string,
  required: boolean,
  inputId: string,
): HTMLLabelElement => {
  const element = document.createElement("label");
  element.className = "fb-label";
  element.htmlFor = inputId;
  element.append(createLabelText(label, required));

  return element;
};

/** Creates a text-like input control with the caller's native `type` and shared wiring. */
export const createTextInput = (
  field: InputFieldDefinition,
  inputId: string,
  helpId: string | undefined,
  inputType: NativeInputType,
): HTMLInputElement => {
  const input = document.createElement("input");
  input.className = "fb-control";
  input.id = inputId;
  input.name = field.key;
  input.placeholder = field.placeholder ?? "";
  input.required = field.required;
  input.type = inputType;

  if (helpId) {
    input.setAttribute("aria-describedby", helpId);
  }

  return input;
};

/** Creates a multiline textarea control sharing the input wiring conventions. */
export const createTextArea = (
  field: InputFieldDefinition,
  inputId: string,
  helpId: string | undefined,
): HTMLTextAreaElement => {
  const textarea = document.createElement("textarea");
  textarea.className = "fb-control fb-textarea";
  textarea.id = inputId;
  textarea.name = field.key;
  textarea.placeholder = field.placeholder ?? "";
  textarea.required = field.required;

  if (helpId) {
    textarea.setAttribute("aria-describedby", helpId);
  }

  return textarea;
};

/** Applies length constraints to a textarea, matching the input length rules. */
export const applyTextAreaLengthValidation = (
  textarea: HTMLTextAreaElement,
  field: InputFieldDefinition,
): void => {
  const rules = field.validationJson;

  if (rules && "minLength" in rules && rules.minLength !== undefined) {
    textarea.minLength = rules.minLength;
  }

  if (rules && "maxLength" in rules && rules.maxLength !== undefined) {
    textarea.maxLength = rules.maxLength;
  }
};

export const applyLengthValidation = (
  input: HTMLInputElement,
  field: InputFieldDefinition,
): void => {
  const rules = field.validationJson;

  if (rules && "minLength" in rules && rules.minLength !== undefined) {
    input.minLength = rules.minLength;
  }

  if (rules && "maxLength" in rules && rules.maxLength !== undefined) {
    input.maxLength = rules.maxLength;
  }
};

export const applyNumberValidation = (
  input: HTMLInputElement,
  rules: NumberValidation | null,
): void => {
  const constraints = getNumberInputConstraints(rules);

  if (constraints.min !== undefined) {
    input.min = String(constraints.min);
  }

  if (constraints.max !== undefined) {
    input.max = String(constraints.max);
  }

  input.step = String(constraints.step);
};

export const createGroupFieldset = (field: ChoiceFieldDefinition): HTMLFieldSetElement => {
  const group = document.createElement("fieldset");
  applyFieldAttributes(group, field);
  group.classList.add("fb-choice-group");

  const legend = document.createElement("legend");
  legend.className = "fb-label";
  legend.append(createLabelText(field.label, field.required));
  group.append(legend);

  return group;
};

export const appendGroupHelpText = (
  group: HTMLFieldSetElement,
  field: ChoiceFieldDefinition,
  context: RenderContext,
): void => {
  if (field.helpText) {
    const helpId = `${context.instanceId}-${toDomId(field.id)}-help`;
    group.setAttribute("aria-describedby", helpId);
    group.append(createHelpText(field.helpText, helpId));
  }
};

export const applyFieldAttributes = (element: HTMLElement, field: PublicFormField): void => {
  element.className = "fb-field";
  element.dataset.fieldId = field.id;
  element.dataset.fieldType = field.type;
};

export const createLabelText = (label: string, required: boolean): DocumentFragment => {
  const fragment = document.createDocumentFragment();
  fragment.append(document.createTextNode(label));

  if (required) {
    const marker = document.createElement("span");
    marker.className = "fb-required";
    marker.setAttribute("aria-hidden", "true");
    marker.textContent = " *";
    fragment.append(marker);
  }

  return fragment;
};

export const createHelpText = (text: string, id: string): HTMLElement => {
  const help = document.createElement("p");
  help.className = "fb-help";
  help.id = id;
  help.textContent = text;

  return help;
};

export const toDomId = (value: string): string => {
  return value.replace(/[^a-zA-Z0-9_-]/g, "-");
};

import { getNumberInputConstraints } from "../form.ts";
